import { useCallback, useState } from 'react'
import {
  updatePartialSubscription,
  usePartialSubscription
} from '../stateData/partialSubscription'
import { useDispatch } from './useDispatch'
import { useExitFlow } from './useExitFlow'
import { useIsUnmounted } from './useIsUnmounted'

type ExitCallbackProps = {
  result: string
  reminder?: boolean
  screenName?: string
  errorCode?: string
  errorDescription?: string
}

export const useExitCallback = ({
  result,
  screenName = undefined,
  reminder = false,
  errorCode = undefined,
  errorDescription = undefined
}: ExitCallbackProps) => {
  const dispatch = useDispatch()
  const partialSubscriptionState = usePartialSubscription()
  const exitFlow = useExitFlow()
  const isUnmounted = useIsUnmounted()
  const [loading, setLoading] = useState(false)

  const onContinue = useCallback(
    async (event) => {
      if (screenName !== undefined) {
        event.target['screenName'] = screenName
      }

      const reminder_code = () => (reminder ? 'cancel_with_reminder' : 'cancel')
      const completion_code = result === 'success' ? 'success' : reminder_code()
      setLoading(true)

      if (partialSubscriptionState.data?.id) {
        try {
          await dispatch(
            updatePartialSubscription({
              sign_up_completion_code: completion_code
            })
          )
        } catch {
          // do nothing
        }
      }

      try {
        // Include error context for failure results
        if (result === 'failure' && errorCode && errorDescription) {
          await exitFlow(result, reminder ? { reminder } : {}, {
            errorCode,
            errorDescription
          })
        } else {
          await exitFlow(result, reminder ? { reminder } : {})
        }
      } catch {
        // do nothing
      }

      if (!isUnmounted()) {
        setLoading(false)
      }
    },
    [
      dispatch,
      exitFlow,
      isUnmounted,
      partialSubscriptionState.data?.id,
      reminder,
      result,
      screenName,
      errorCode,
      errorDescription
    ]
  )

  return { onContinue, loading }
}
